/**
 * @prop --height: Height of button
 * @prop --padding-top: Padding top of button
 * @prop --padding-bottom: Padding bottom of button
 * @prop --padding-left: Padding left of button
 * @prop --padding-right: Padding right of button
 * @prop --border-radius: Border radius of button
 * @prop --color: Color of button
 * @prop --background-color: Background color of button
 * @prop --border-color: Border color of button
 * @prop --icon-size: Size of icon next to button
 * @prop --hover-color: Hover color of button
 * @prop --hover-background-color: Hover background color of button
 * @prop --hover-border-color: Hover border color of button
 * @prop --white-space: White space behaviour of button
 *
 * @prop --stzh-form-input-height: **Global**: Height of fields & buttons
 * @prop --stzh-form-input-small-height: **Global**: Height of fields & buttons when small variant is used
 * @prop --stzh-form-input-tiny-height: **Global**: Height of fields & buttons when tiny variant is used
 */

@mixin button--size-default() {
  --height: #{$formInputHeight};
  --padding-top: calc(#{space("xsmall")} - var(--border-width));
  --padding-bottom: calc(#{space("xsmall")} - var(--border-width));
  --padding-left: #{space("xlarge")};
  --padding-right: #{space("xlarge")};
  --font-size: var(--stzh-font-centi-font-size);
  --line-height: var(--stzh-font-centi-text-line-height);
  --letter-spacing: normal;
  --icon-size: #{iconSize("medium")};
  --icon-toggle-size: 14.5px;

  &:where([icon-only]:not([icon-only="false"])) {
    --icon-size: #{iconSize("large")};
  }
}

@mixin button--size-small() {
  --height: #{$formInputHeightSmall};
  --padding-left: #{space("large")};
  --padding-right: #{space("large")};
  --font-size: var(--stzh-font-milli-font-size);
  --line-height: var(--stzh-font-milli-text-line-height);
  --icon-size: #{iconSize("medium")};
}

@mixin button--size-tiny() {
  --height: #{$formInputHeightTiny};
  --padding-left: #{space("medium")};
  --padding-right: #{space("medium")};
  --padding-top: calc(#{space("xxsmall")} - var(--border-width));
  --padding-bottom: calc(#{space("xxsmall")} - var(--border-width));
  --font-size: var(--stzh-font-milli-font-size);
  --line-height: var(--stzh-font-milli-text-line-height);
  --icon-size: #{iconSize("medium")};
}

:host {
  @include button--size-default;
  --color: var(--stzh-button-color, #{$colorWhite});
  --background-color: var(--stzh-button-background-color, #{$colorPrimary70});
  --border-width: 0px;
  --border-color: transparent;
  --border-radius: 0px;
  --icon-text-margin: #{space("xsmall")};
  --icon-toggle-color: currentColor;
  --badge-icon-text-margin: calc(var(--icon-text-margin) + #{space("xxsmall")});
  --white-space: var(--stzh-button-white-space, normal);
  --box-shadow: none;
  --cursor: pointer;

  --hover-color: var(--stzh-button-hover-color, #{$colorWhite});
  --hover-background-color: var(
    --stzh-button-hover-background-color,
    #{$colorSecondary60}
  );
  --hover-border-color: transparent;

  display: inline-flex;
  width: auto;
  min-width: var(--height);
  min-height: var(--height);
  pointer-events: var(--stzh-button-pointer-events, auto);

  @media print {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  &[icon-only]:not([icon-only="false"]) {
    --padding-top: 0;
    --padding-bottom: 0;
    --padding-left: 0;
    --padding-right: 0;

    width: var(--height);
    height: var(--height);
  }

  &[fullwidth]:not([fullwidth="false"]) {
    width: 100%;
    display: grid;
    flex: 0 0 100%;
  }

  &[rounded]:not([rounded="false"]) {
    --border-radius: var(--height);
  }

  &[no-padding-left]:not([no-padding-left="false"]) {
    --padding-left: 0px;
  }

  &[no-padding-right]:not([no-padding-right="false"]) {
    --padding-right: 0px;
  }

  &[variant="secondary"] {
    --color: var(--stzh-button-secondary-color, #{$colorPrimary70});
    --background-color: var(
      --stzh-button-secondary-background-color,
      #{$colorSecondary30}
    );

    --hover-color: var(--stzh-button-secondary-hover-color, #{$colorPrimary80});
    --hover-background-color: var(
      --stzh-button-secondary-hover-background-color,
      #{$colorSecondary40}
    );
  }

  &[variant="tertiary"] {
    --color: var(--stzh-button-tertiary-color, #{$colorPrimary70});
    --background-color: var(
      --stzh-button-tertiary-background-color,
      transparent
    );

    --hover-color: var(--stzh-button-tertiary-hover-color, #{$colorPrimary80});
    --hover-background-color: var(
      --stzh-button-tertiary-hover-background-color,
      #{$colorSecondary20}
    );
  }

  &[variant="tertiary-plain"] {
    --color: var(--stzh-button-tertiary-color, #{$colorPrimary70});
    --background-color: transparent;

    --hover-color: var(--stzh-button-tertiary-hover-color, #{$colorPrimary80});
    --hover-background-color: var(--background-color);
  }

  &[active]:not([active="false"]) {
    --color: var(--stzh-button-active-color, #{$colorWhite});
    --background-color: var(
      --stzh-button-active-background-color,
      #{$colorSecondary60}
    );
  }

  &[active][variant="secondary"]:not([active="false"]) {
    --color: var(--stzh-button-secondary-hover-color, #{$colorPrimary80});
    --background-color: var(
      --stzh-button-secondary-hover-background-color,
      #{$colorSecondary40}
    );
  }

  &[active][variant="tertiary"]:not([active="false"]) {
    --color: var(--stzh-button-tertiary-hover-color, #{$colorPrimary80});
    --background-color: var(
      --stzh-button-tertiary-hover-background-color,
      #{$colorSecondary20}
    );
  }

  &[active][variant="tertiary-plain"]:not([active="false"]) {
    --color: var(--stzh-button-tertiary-hover-color, #{$colorPrimary80});
    --background-color: transparent;
  }

  &[disabled]:not([disabled="false"]),
  &[a11y-disabled]:not([a11y-disabled="false"]) {
    --color: var(--stzh-button-disabled-color, #{$colorWhite});
    --background-color: var(
      --stzh-button-disabled-background-color,
      #{$colorGrey70}
    );

    --hover-color: var(--color);
    --hover-background-color: var(--background-color);
  }

  &[disabled]:not([disabled="false"])[variant="secondary"],
  &[a11y-disabled]:not([a11y-disabled="false"])[variant="secondary"] {
    --color: var(--stzh-button-secondary-disabled-color, #{$colorGrey80});
    --background-color: var(
      --stzh-button-secondary-disabled-background-color,
      #{$colorGrey20}
    );
  }

  &[disabled]:not([disabled="false"])[variant="input"],
  &[a11y-disabled]:not([a11y-disabled="false"])[variant="input"] {
    --color: var(--stzh-button-input-disabled-color, #{$colorGrey80});
    --background-color: var(
      --stzh-button-input-disabled-background-color,
      #{$colorGrey20}
    );
  }

  &[disabled]:not([disabled="false"])[variant="tertiary"],
  &[disabled]:not([disabled="false"])[variant="tertiary-plain"],
  &[a11y-disabled]:not([a11y-disabled="false"])[variant="tertiary"],
  &[a11y-disabled]:not([a11y-disabled="false"])[variant="tertiary-plain"] {
    --color: var(--stzh-button-tertiary-disabled-color, #{$colorGrey70});
    --background-color: var(
      --stzh-button-tertiary-disabled-background-color,
      transparent
    );
  }
}

:host(:where([size="small"])) {
  @include button--size-small;
}

:host(:where([size="tiny"])) {
  @include button--size-tiny;
}

@each $breakpoint, $size in $breakpoints {
  @include mq($from: $breakpoint) {
    :host(:where([size-#{$breakpoint}="default"])) {
      @include button--size-default;
    }

    :host(:where([size-#{$breakpoint}="small"])) {
      @include button--size-small;
    }

    :host(:where([size-#{$breakpoint}="tiny"])) {
      @include button--size-tiny;
    }
  }
}

@keyframes stzh-button-effect-cta {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateX(0);
  }

  40% {
    transform: translateX(-8px);
  }

  60% {
    transform: translateX(-4px);
  }
}

.stzh-button {
  @include font("heavy");
  font-size: var(--font-size);
  line-height: var(--line-height);
  letter-spacing: var(--letter-spacing);
  position: relative;
  z-index: 0;
  overflow: visible;
  display: flex;
  align-items: center;
  justify-content: stretch;
  appearance: none;
  color: var(--color);
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);
  padding-left: var(--padding-left);
  padding-right: var(--padding-right);
  background-color: var(--background-color);
  transition-duration: $baseTransitionAnimationSpeed;
  transition-property: color, background-color, border-color;
  border-style: solid;
  border-width: var(--border-width);
  border-color: var(--border-color);
  cursor: var(--cursor);
  text-decoration-line: none;
  width: 100%;
  min-height: 100%;
  margin: 0;
  border-radius: var(--border-radius);
  text-align: left;
  box-shadow: var(--box-shadow);

  &:hover {
    border-color: var(--hover-border-color);
    background-color: var(--hover-background-color);
    color: var(--hover-color);
  }

  &__vhidden {
    @include visuallyhidden;
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
  }

  &__icon-wrapper,
  &__toggle-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__icon-wrapper {
    --stzh-icon-size: var(--icon-size);
    width: auto;
    height: 1em;
  }

  &__toggle-icon-wrapper {
    width: var(--icon-size);
    height: var(--icon-size);
  }

  &__toggle-icon {
    position: relative;
    display: block;
    width: var(--icon-toggle-size);
    height: var(--icon-toggle-size);

    &::before,
    &::after {
      position: absolute;
      top: 50%;
      left: 50%;
      content: "";
      display: block;
      width: var(--icon-toggle-size);
      height: 2px;
      transition-property: transform, background-color;
      transition-duration: 500ms;
      transform-origin: top left;
      background-color: var(--icon-toggle-color);

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }

    &::before {
      transform: rotate(180deg) translate(-50%, -50%);
    }

    &::after {
      transform: rotate(90deg) translate(-50%, -50%);
    }
  }

  &__text {
    @include wordWrap;
    white-space: var(--white-space);
    text-align: center;
  }

  &--not-hyphenated &__text {
    hyphens: none;
  }

  &__icon-wrapper:not(:empty) + &__text:not(:empty),
  &__text:not(:empty) + &__icon-wrapper:not(:empty) {
    margin-left: var(--icon-text-margin);
  }

  &__badge {
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
  }

  &__input {
    @include visuallyhiddenInput;
  }

  &__mark {
    @include radio__mark();
    width: 20px;
    height: 20px;
    margin-right: space("xsmall");
    border-color: currentColor;
  }

  &__check {
    @include radio__check();
  }

  /* Hover / Focus / Checked / Disabled of Radio Type */

  &__input:checked:hover ~ &__inner &__mark &__check,
  &__input:checked:focus ~ &__inner &__mark &__check {
    color: $colorSecondary60;
  }

  &:hover &__mark,
  &__input:focus:hover ~ &__inner &__mark,
  &__input:checked:hover ~ &__inner &__mark,
  &__input:checked:focus ~ &__inner &__mark {
    border-color: $colorSecondary60;
  }

  &__input:focus ~ &__inner &__mark,
  &__input:checked ~ &__inner &__mark {
    border-color: $colorPrimary;
  }

  &__input:checked ~ &__inner &__mark &__check {
    opacity: 1;
  }

  &--is-disabled &__input ~ &__inner &__mark &__check {
    color: $formDisabledColor;
  }

  &--is-disabled &__input ~ &__inner &__mark {
    border-color: $formDisabledBorderColor;
  }

  &--is-disabled &__mark {
    background-color: $formDisabledBackgroundColor;
  }

  /* Is expanded */

  &--is-expanded &__toggle-icon {
    &::before,
    &::after {
      transform: rotate(0) translate(-50%, -50%);
    }
  }

  /* Badge positioning variants */

  &--badge-position-button &__icon-wrapper {
    position: static;
  }

  &__badge,
  &--badge-position-button &__badge {
    top: calc(#{space("xsmall")} * -1);
    right: calc(#{space("xsmall")} * -1);
  }

  &--badge-position-icon#{&}--has-icon &__icon-wrapper {
    position: relative;
  }

  &--badge-position-icon#{&}--has-icon &__badge {
    top: calc(#{space("xsmall")} * -1);
    right: calc(#{space("small")} * -1);
  }

  &--badge-position-icon#{&}--has-icon#{&}--has-badge
    &__icon-wrapper:not(:empty)
    + &__text:not(:empty),
  &--badge-position-icon#{&}--has-icon#{&}--has-badge
    &__text:not(:empty)
    + &__icon-wrapper:not(:empty) {
    margin-left: var(--badge-icon-text-margin);
  }

  /* Variant if button only has icon */

  &--has-icon-only &__text {
    @include visuallyhidden;
  }

  /* Effect */

  &--effect-cta:hover &__icon-wrapper {
    animation: stzh-button-effect-cta 1s;
  }

  /* Alignment variants */

  &--align-left &__inner {
    justify-content: flex-start;
  }

  &--align-right &__inner {
    justify-content: flex-end;
  }

  &--align-center &__inner {
    justify-content: center;
  }

  &--align-space-between &__inner {
    justify-content: space-between;
  }

  /* Disabled variant */

  &--is-disabled {
    cursor: not-allowed;
  }

  /* Default */

  &--default.has-focus {
    outline: var(--stzh-flyingfocus-color) solid 3px;
  }
}
